import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { DollarSign, RefreshCw, TrendingDown, TrendingUp, Repeat } from "lucide-react";
import { useApi } from "@/lib/api";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const label = (m) => new Date(`${m}-01T00:00:00Z`).toLocaleDateString(undefined, { month: "short", year: "2-digit", timeZone: "UTC" });

const Stat = ({ icon: Icon, title, value, sub, testid }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500"><Icon size={14} /> {title}</div>
    <div className="font-heading text-3xl font-bold mt-2 tabular-nums">{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

export default function RevenuePanel() {
  const api = useApi();
  const qc = useQueryClient();
  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: ["admin-revenue"],
    queryFn: async () => (await api.get("/admin/revenue")).data,
  });
  const refresh = async () => {
    const fresh = (await api.get("/admin/revenue?refresh=true")).data;
    qc.setQueryData(["admin-revenue"], fresh);
  };

  if (isLoading) return <div className="h-64 bg-white border border-slate-200 rounded-lg animate-pulse" data-testid="revenue-loading" />;
  if (isError) return <div className="bg-white border border-red-200 text-red-700 rounded-lg p-5" data-testid="revenue-error">Couldn't load revenue from Stripe.</div>;
  const series = data.series.map((s) => ({ ...s, label: label(s.month) }));
  const t = data.this_month;

  return (
    <section className="space-y-5" data-testid="revenue-panel">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Revenue</h2>
          <p className="text-xs text-slate-500 mt-1">From Stripe. Updated {new Date(data.generated_at).toLocaleTimeString()}.</p>
        </div>
        <button data-testid="revenue-refresh-btn" onClick={refresh} className="h-9 px-3 rounded-md border border-slate-200 bg-white text-sm hover:bg-slate-100 flex items-center gap-2">
          <RefreshCw size={14} className={isFetching ? "animate-spin" : ""} /> Refresh
        </button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat testid="revenue-mrr" icon={Repeat} title="MRR" value={money(data.mrr)} sub={`${data.active_subscriptions} active (${data.by_kind.individual || 0} solo, ${data.by_kind.team || 0} team)`} />
        <Stat testid="revenue-this-month" icon={DollarSign} title="Revenue this month" value={money(t.revenue)} sub={`${money(data.total_12m)} last 12 months`} />
        <Stat testid="revenue-signups" icon={TrendingUp} title="New subscriptions" value={t.signups} sub="this month" />
        <Stat testid="revenue-cancellations" icon={TrendingDown} title="Cancellations" value={t.cancellations} sub="this month" />
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="revenue-chart">
          <h3 className="text-sm font-semibold mb-3">Monthly revenue</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={series}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} angle={-35} textAnchor="end" height={45} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} width={50} />
              <Tooltip formatter={(v) => money(v)} />
              <Bar dataKey="revenue" name="Revenue" fill="#1E293B" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="signups-chart">
          <h3 className="text-sm font-semibold mb-3">Sign-ups vs cancellations</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={series}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} angle={-35} textAnchor="end" height={45} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={30} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="signups" name="Sign-ups" fill="#2563EB" radius={[4, 4, 0, 0]} />
              <Bar dataKey="cancellations" name="Cancellations" fill="#F59E0B" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </section>
  );
}
